import React from 'react';
import { Avatar, Menu } from '@parsina/framework';
import { CREDIT_LEVEL_META, soft } from '@parsina/framework/tokens';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n';
import { useShortcutHelp } from '../../lib/shortcuts.jsx';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';

/**
 * The foot of the clinic sidebar.
 *
 * It replaced three stacked things that did not know about each other: a row
 * that held nothing but the collapse toggle, a bordered card for the person's
 * پارسه, and a borderless identity row under it — three shapes, two chevrons
 * pointing in different directions, and a caption («پنل مدیریت») that named
 * the product instead of the person. The rebuild answers the footer's three
 * questions in two grouped pieces, top to bottom:
 *
 * ── THE UTILITY STRIP ───────────────────────────────────────────────────────
 * Shell controls, not account information: the collapse toggle in the icon
 * column (where its hit target survives the rail) and, beside it, the way into
 * the `?` overlay. The strip used to be a 48px row spent on one 32px button;
 * the overlay is the only place a shortcut is listed, so a visible entry earns
 * the rest of the row in a panel that is designed keyboard-first.
 *
 * ── THE ACCOUNT BLOCK ───────────────────────────────────────────────────────
 * One card, inset like the nav pills above it, holding the PERSON: who is
 * signed in (the menu trigger) and, when they own one, their own پارسه as a
 * second row of the same card. Grouping the balance under the name is what says
 * whose money it is — better than the label it used to carry, which repeated
 * the unit («اعتبار پارسهٔ شما … ۵۰۰ پارسه»). There is no clinic pool: every
 * member of staff has their own wallet and pays for what they do. A balance that
 * could not be read leaves a one-row card, not an empty one.
 *
 * The level word is shown only when it carries information. «کافی» beside a
 * green dot said nothing twice; «کم» is now a red row with a «شارژ» chip in it,
 * «متوسط» an amber word, and a fine balance is simply the number.
 *
 * The balance row stays a real <a> to حساب پارسینا — middle-click and «open in
 * new tab» keep working — and the trigger stays a <button>. They are siblings
 * in the card, never nested, so neither is an interactive element inside
 * another.
 *
 * ── THE 68px RAIL ───────────────────────────────────────────────────────────
 * The icon column is first in DOM order, so under RTL it is rightmost and the
 * collapse — which clips from the inline-end — takes only text. The balance
 * row is NOT rendered when collapsed rather than faded: a faded link keeps its
 * tab stop. Its level moves onto the avatar as a status dot, and the trigger's
 * tooltip carries the amount, so the rail still answers «how much?».
 *
 * Props:
 *   collapsed / onToggleCollapse
 *   user        {name, subtitle?}          — subtitle only when it says something
 *                                            the name does not (email, phone)
 *   credit      {amount, level, unit, href} | null — omit to hide the balance row
 *   items       [{key, label, description?, icon?, href?, external?, onClick?,
 *                 tone?, separatorBefore?, 'data-testid'?}]
 *   menuLabel   accessible name of the menu; the trigger's own text is a name
 */

const MINI = 68;                 // the collapsed rail
const INSET = 10;                // the card's inset, matching the nav pills
const CELL = MINI - INSET * 2;   // the icon column inside an inset card

// Ink on the sidebar gradient. Kept here rather than in the theme because the
// gradient is the only dark surface in this app and these are its alphas.
const INK = {
  strong: '#fff',
  body: 'rgba(255,255,255,0.72)',
  muted: 'rgba(255,255,255,0.45)',
  faint: 'rgba(255,255,255,0.3)',
  line: 'rgba(255,255,255,0.08)',
  fill: 'rgba(255,255,255,0.05)',
  fillHover: 'rgba(255,255,255,0.10)',
  fillActive: 'rgba(255,255,255,0.14)',
};

const CoinGlyph = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" fill="none" width={size} height={size} aria-hidden>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7.5v9M9.5 10h5M9.5 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// Says the press LEAVES this panel.
const ExternalGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden
    style={{ flexShrink: 0, opacity: 0.55 }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden
    style={{
      flexShrink: 0, color: open ? INK.strong : INK.muted,
      transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s, color 0.18s',
    }}
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

// ── the utility strip ───────────────────────────────────────────────────────
function CollapseToggle({ collapsed, onToggle, t }) {
  const label = collapsed ? t('nav.menu.open') : t('nav.menu.close');
  return (
    <button
      type="button"
      onClick={onToggle}
      title={label}
      aria-label={label}
      aria-expanded={!collapsed}
      data-testid="sidebar-collapse"
      style={{
        width: 32, height: 32,
        background: INK.line,
        border: 'none', borderRadius: 9,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: INK.muted,
        transition: 'background 0.18s, color 0.18s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = INK.fillActive; e.currentTarget.style.color = INK.strong; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = INK.line; e.currentTarget.style.color = INK.muted; }}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"
        style={{
          transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
          transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
        }}>
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
      </svg>
    </button>
  );
}

function ShortcutsEntry({ t }) {
  const help = useShortcutHelp();
  return (
    <button
      type="button"
      onClick={help.toggle}
      data-testid="sidebar-shortcuts"
      aria-pressed={help.open}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        height: 32, padding: '0 8px', marginInlineEnd: INSET,
        background: 'transparent', border: 'none', borderRadius: 9,
        color: INK.muted, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
        transition: 'background 0.18s, color 0.18s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = INK.line; e.currentTarget.style.color = INK.body; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = INK.muted; }}
    >
      {/* A key cap, so the row teaches the key it stands in for. */}
      <kbd
        aria-hidden
        style={{
          minWidth: 18, height: 18, padding: '0 4px', borderRadius: 5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: INK.line, border: `1px solid ${INK.line}`,
          color: 'inherit', fontFamily: 'inherit', fontSize: 11, fontWeight: 800, lineHeight: 1,
        }}
      >
        ?
      </kbd>
      <span>{t('nav.footer.shortcuts')}</span>
    </button>
  );
}

// ── one menu row ────────────────────────────────────────────────────────────
function MenuRow({ item, onDone, testIdBase }) {
  const destructive = item.tone === 'destructive';
  const ink = destructive ? C.red : C.textPrimary;
  const hover = destructive ? soft(C.red, 0.10) : soft(C.navy, 0.08);

  const shared = {
    role: 'menuitem',
    'data-testid': item['data-testid'] || `${testIdBase}-item-${item.key}`,
    style: {
      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
      padding: '9px 10px', minHeight: 40,
      border: 'none', borderRadius: 9, background: 'transparent',
      color: ink, textAlign: 'start', textDecoration: 'none',
      fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
      cursor: 'pointer', transition: 'background 0.14s',
    },
    onMouseEnter: (e) => { e.currentTarget.style.background = hover; },
    onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent'; },
  };

  const body = (
    <>
      {item.icon ? (
        <span aria-hidden style={{ flexShrink: 0, display: 'flex', color: destructive ? C.red : C.navy }}>
          {item.icon}
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.label}
        </span>
        {item.description ? (
          <span dir="auto" style={{
            display: 'block', fontSize: 11, fontWeight: 500, marginTop: 1, color: C.textMuted,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {item.description}
          </span>
        ) : null}
      </span>
      {item.external ? <ExternalGlyph /> : null}
    </>
  );

  // A real <a> when the destination is a URL: focusable, openable in a new tab
  // with the usual modifiers, and readable in the status bar.
  return item.href ? (
    <a {...shared} href={item.href} onClick={() => onDone?.()}>{body}</a>
  ) : (
    <button {...shared} type="button" onClick={() => { onDone?.(); item.onClick?.(); }}>{body}</button>
  );
}

// ── the balance row ─────────────────────────────────────────────────────────
function BalanceRow({ credit, t, testId }) {
  const level = credit.level === 'low' || credit.level === 'mid' ? credit.level : 'ok';
  const color = CREDIT_LEVEL_META[level].color;
  const low = level === 'low';
  // «شارژ» only where credit is sold and the account app can take the payment
  // (hooks/useCreditPurchase.js); the red row says «low» either way.
  const topUp = useCreditPurchase(low);
  const sentence = t('nav.credits.title', { amount: credit.amount });

  const bg = (hovered) => (low
    ? soft(C.red, hovered ? 0.26 : 0.16)
    : (hovered ? INK.fillHover : 'transparent'));

  return (
    <a
      href={credit.href}
      data-testid={`${testId}-credit`}
      aria-label={t('nav.credits.aria', { amount: credit.amount })}
      title={sentence}
      style={{
        display: 'flex', alignItems: 'center', height: 38,
        borderTop: `1px solid ${INK.line}`,
        background: bg(false), color: 'inherit', textDecoration: 'none',
        transition: 'background 0.16s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = bg(true); }}
      onMouseLeave={(e) => { e.currentTarget.style.background = bg(false); }}
    >
      <span aria-hidden style={{ width: CELL, flexShrink: 0, display: 'flex', justifyContent: 'center', color }}>
        <CoinGlyph size={16} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 4, whiteSpace: 'nowrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: INK.strong }}>{credit.amount}</span>
        <span style={{ fontSize: 11, color: INK.muted }}>{credit.unit}</span>
      </span>
      {/* The level, only when it is news. */}
      {low ? (topUp ? (
        <span style={{
          marginInlineEnd: INSET, flexShrink: 0,
          padding: '3px 8px', borderRadius: 999,
          background: C.teal, color: INK.strong,
          fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap',
        }}>
          {t('nav.credits.topUpShort')}
        </span>
      ) : null) : level === 'mid' ? (
        <span style={{ marginInlineEnd: INSET + 2, flexShrink: 0, fontSize: 10.5, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
          {t('nav.credits.levelMid')}
        </span>
      ) : null}
    </a>
  );
}

export default function SidebarFooter({
  collapsed = false,
  onToggleCollapse,
  user,
  credit = null,
  items = [],
  menuLabel,
  'data-testid': testId = 'account-card',
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef(null);

  // The anchor moves 192px under a CSS transition when the rail collapses; a
  // menu left open would float beside where the trigger used to be.
  React.useEffect(() => { setOpen(false); }, [collapsed]);
  const close = React.useCallback(() => setOpen(false), []);

  const rows = items.filter(Boolean);
  const name = user?.name || '—';
  const level = credit ? (CREDIT_LEVEL_META[credit.level] ? credit.level : 'ok') : null;
  const levelColor = level ? CREDIT_LEVEL_META[level].color : null;

  // When collapsed the balance row is gone, so the tooltip says what it said.
  const tooltip = [`${menuLabel} — ${name}`, credit ? t('nav.credits.title', { amount: credit.amount }) : null]
    .filter(Boolean).join('\n');

  const fade = {
    opacity: collapsed ? 0 : 1,
    pointerEvents: collapsed ? 'none' : 'auto',
    transition: 'opacity 0.16s ease',
  };

  return (
    <div data-testid={testId} style={{ flexShrink: 0, borderTop: `1px solid ${INK.line}`, paddingTop: 6 }}>

      {/* ── utility strip ── */}
      <div style={{ display: 'flex', alignItems: 'center', height: 40 }}>
        <div style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} t={t} />
        </div>
        {/* Not rendered when collapsed: a faded button keeps its tab stop. */}
        {!collapsed ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
            <ShortcutsEntry t={t} />
          </div>
        ) : null}
      </div>

      {/* ── account block ── */}
      <div
        style={{
          width: `calc(100% - ${INSET * 2}px)`, margin: `4px ${INSET}px ${INSET}px`,
          borderRadius: 12, overflow: 'hidden',
          background: INK.fill, border: `1px solid ${INK.line}`,
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
        }}
      >
        <button
          ref={triggerRef}
          type="button"
          data-testid={`${testId}-trigger`}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${menuLabel} — ${name}`}
          title={collapsed ? tooltip : undefined}
          style={{
            width: '100%', height: 52, padding: 0,
            display: 'flex', alignItems: 'center',
            border: 'none', background: open ? INK.fillActive : 'transparent',
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'start',
            color: 'inherit', overflow: 'hidden', transition: 'background 0.16s',
          }}
          onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = INK.fillHover; }}
          onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
        >
          {/* Avatar — first in RTL → rightmost → the one thing the rail keeps */}
          <span style={{ width: CELL, flexShrink: 0, display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <Avatar name={name} size={34} bg={C.navy} src={user?.avatarUrl || undefined} />
            {/* The balance's level survives the rail as a dot on the avatar. */}
            {collapsed && levelColor ? (
              <span
                aria-hidden
                style={{
                  position: 'absolute', insetBlockEnd: 1, insetInlineStart: 7,
                  width: 10, height: 10, borderRadius: '50%',
                  // A dark ring, not a brand hex: it must read against any sidebar gradient.
                  background: levelColor, boxShadow: '0 0 0 2px rgba(0,0,0,0.45)',
                }}
              />
            ) : null}
          </span>

          <span style={{ ...fade, display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, paddingInlineEnd: INSET + 2 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span dir="auto" style={{
                display: 'block', color: INK.strong, fontSize: 13, fontWeight: 700,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {name}
              </span>
              {user?.subtitle ? (
                <span dir="auto" style={{
                  display: 'block', color: INK.muted, fontSize: 11, marginTop: 1,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {user.subtitle}
                </span>
              ) : null}
            </span>
            <Chevron open={open} />
          </span>
        </button>

        {credit && !collapsed ? <BalanceRow credit={credit} t={t} testId={testId} /> : null}
      </div>

      {/* 'top': this block is the LAST thing in the sidebar. */}
      <Menu
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        ariaLabel={menuLabel}
        placement="top"
        width={240}
        data-testid={`${testId}-menu`}
      >
        {/* Who this menu is about — the trigger's text is gone behind the menu. */}
        <div style={{ padding: '6px 10px 8px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
          <div dir="auto" style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </div>
          {user?.subtitle ? (
            <div dir="auto" style={{ fontSize: 11, color: C.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.subtitle}
            </div>
          ) : null}
        </div>
        {rows.map((item) => (
          <React.Fragment key={item.key}>
            {item.separatorBefore ? (
              <div role="separator" style={{ height: 1, background: C.border, margin: '5px 8px' }} />
            ) : null}
            <MenuRow item={item} onDone={close} testIdBase={testId} />
          </React.Fragment>
        ))}
      </Menu>
    </div>
  );
}
